import { getFunctions, httpsCallable } from 'firebase/functions';
import { firebaseConfig, auth, appCheck } from '../../firebase';
import { getToken } from 'firebase/app-check';

/*
    Hook for working with cloud functions
*/
const useCloud = () => {
  const functions = getFunctions();
  if (process.env.NODE_ENV === 'development') {
    //connectFunctionsEmulator(functions, 'localhost', 5001);
  }

  /*
    converts a document in the "types" collection to a "model" in the "models" collection

    contentType: {
      name: 'App',
      type: 'app'
    }

    document: supply a types document json instead of loading the json for contentType.name from "types" collection

  */
  const typeToModel = async ({ contentType, document, overwrite = false }) => {
    const converterFunction = httpsCallable(functions, 'data_type_to_model', {
      limitedUseAppCheckTokens: true
    });

    const result = await converterFunction({
      contentType: {
        name: contentType.name,
        type: contentType.type
      },
      document,
      overwrite
    });

    return result;
  };

  /* deletes a document and all its subcollections */
  const deepDelete = async ({ collectionPath, id, databaseId }) => {
    const deepDeepFunction = httpsCallable(functions, 'data_delete_doc', {
      limitedUseAppCheckTokens: true
    });

    const result = await deepDeepFunction({
      collectionPath,
      id,
      databaseId
    });

    return result;
  };

  const pexelsSearch = async ({ keywords, perPage, page }) => {
    const pexelsFunction = httpsCallable(functions, 'integration_pexels', {
      limitedUseAppCheckTokens: true
    });

    const result = await pexelsFunction({
      query: keywords,
      perPage,
      page
    });

    return result;
  };

  /*  
    The FacebookPageConnect and LinkedInConnect modal gets a user access token for each connected asset,
      We create an oauth connection object for each.

    assets should be like: [{ tokenUserShort, assetId, assetType, assetName, userId, provider}]

  */

  const setupOAuthConnections = async assets => {
    const oAuthSetupFunction = httpsCallable(
      functions,
      'integration_oauth_setup',
      {
        limitedUseAppCheckTokens: true
      }
    );

    const result = await oAuthSetupFunction(assets);

    return result;
  };

  /* deletes a document and all its subcollections */
  const deleteUser = async ({ uid }) => {
    const deleteUserFunction = httpsCallable(functions, 'user_delete', {
      limitedUseAppCheckTokens: true
    });

    const result = await deleteUserFunction({
      uid
    });

    return result;
  };

  /* 
    Purpose: Import one or more documents into the local firestore database

    data: 

    [
      {
          document: {...fields }, 
          "_type": {
              "name": "Type",
              "plural": "Types",
              "type": "type",
              "hasOwnCollection": true,
              "path": "models/XDLW32Wmd2wmC2oPaeON"
          }
      _related: (optional) [ ... repeated structure of documents, _related, _type]
    },
          ... more
    ]

    */
  const importData = async options => {
    const importFunction = httpsCallable(functions, 'data_import', {
      limitedUseAppCheckTokens: true
    });

    const result = await importFunction(options);

    return result;
  };

  /*
  Purpose: Get a document or documents by path.

  request examples:

  {
    "password": "whatever",
    "path" : "animals/XDLW32Wmd2wmC2oPaeON"
  }
  {
    "password": "whatever",
    "path" : "animals"
  }
  {
    "password": "whatever",
    "path" : "animals/id/zoo/id/whatever"
  }

  returns:

  [{ 
      document: {...fields }, 
			"_type": {
		        "name": "Type",
		        "plural": "Types",
		        "type": "type",
		        "hasOwnCollection": true,
		        "path": "models/XDLW32Wmd2wmC2oPaeON"
		    }
			_related: (optional) [ ... repeated structure of documents, _related, _type]
  },... more docs if we exported a collection]

*/
  const exportData = async ({
    path,
    flatteningAllowed,
    databaseId,
    contentTypeName
  }) => {
    const exportFunction = httpsCallable(functions, 'data_export', {
      limitedUseAppCheckTokens: true
    });

    const result = await exportFunction({
      path,
      flatteningAllowed,
      databaseId,
      contentTypeName
    });

    return result;
  };

  const moveDocument = async ({
    sourcePath,
    targetPath,
    recursive,
    deleteOnMove
  }) => {
    const moveFunction = httpsCallable(functions, 'data_move_doc', {
      limitedUseAppCheckTokens: true
    });

    const result = await moveFunction({
      sourcePath,
      targetPath,
      recursive,
      deleteOnMove
    });

    return result;
  };

  const callFunction = async (functionName, data, useOnRequest) => {
    if (!useOnRequest) {
      const dynamicFunctionCall = httpsCallable(functions, functionName, {
        limitedUseAppCheckTokens: true
      });

      const result = await dynamicFunctionCall(data);

      return result;
    }

    try {
      const appCheckTokenResponse = await getToken(
        appCheck
        /* forceRefresh= false*/
      );

      const headers = {
        'Content-Type': 'application/json',
        'X-Firebase-AppCheck': appCheckTokenResponse.token
      };

      let url = `https://us-central1-${firebaseConfig.projectId}.cloudfunctions.net/${functionName}`;
      if (window.location.href.includes('localhost:')) {
        url = `http://127.0.0.1:5001/${firebaseConfig.projectId}/us-central1/${functionName}`;
      }

      const currentUser = auth.currentUser;
      if (currentUser) {
        const idToken = await currentUser.getIdToken();
        headers['Authorization'] = `Bearer ${idToken}`;
      }

      const response = await fetch(url, {
        method: 'POST',
        headers,
        body: JSON.stringify({
          data
        }),
        mode: 'cors'
      });

      const result = await response.json();
      return result?.data || result;
    } catch (error) {
      return { error };
    }
  };

  const generatePdf = async ({ templateId, documentPath, blockEmailing }) => {
    const pdfFunction = httpsCallable(functions, 'produce_pdf', {
      limitedUseAppCheckTokens: true
    });
    const result = await pdfFunction({
      templateId,
      documentPath,
      blockEmailing
    });
    return result;
  };

  const regenerateReportSection = async ({
    databaseId,
    reportId,
    reportInstanceId,
    organisationId,
    sectionId,
    path,
    outputPath,
    componentId,
    prompt,
    componentDataFunction,
    componentCalculation
  }) => {
    const regenFunction = httpsCallable(
      functions,
      'produce_report_section_again',
      {
        limitedUseAppCheckTokens: true
      }
    );
    const result = await regenFunction({
      databaseId,
      reportId,
      reportInstanceId,
      organisationId,
      sectionId,
      path,
      outputPath,
      componentId,
      prompt,
      componentDataFunction,
      componentCalculation
    });
    return result;
  };

  return {
    typeToModel,
    importData,
    moveDocument,
    exportData,
    deepDelete,
    generatePdf,
    deleteUser,
    callFunction,
    pexelsSearch,
    setupOAuthConnections,
    regenerateReportSection
  };
};

export default useCloud;
